import {
  MIN_PLAYERS,
  type ClientRoomState,
  type ContentMode,
} from "@poppycock/shared";
import { motion } from "framer-motion";
import { useState } from "react";
import { InvitePair } from "./InviteButton";
import { PlayerList } from "./PlayerList";
import { BrandMark, Button, Card, PhaseBadge } from "./ui";

const MODE_OPTIONS: {
  id: ContentMode;
  label: string;
  blurb: string;
}[] = [
  {
    id: "text",
    label: "All text",
    blurb: "Classic typed answers every round",
  },
  {
    id: "design",
    label: "All drawings",
    blurb: "Shape illustrations every round",
  },
  {
    id: "caption",
    label: "Caption contest",
    blurb: "Same ridiculous photo — write the funniest caption",
  },
  {
    id: "mix",
    label: "Mix",
    blurb: "Cycle text → drawing → caption…",
  },
];

export function LobbyScreen({
  state,
  isHost,
  onStart,
  onSetContentMode,
  onLeave,
}: {
  state: ClientRoomState;
  isHost: boolean;
  onStart: (contentMode: ContentMode) => void;
  onSetContentMode: (contentMode: ContentMode) => void;
  onLeave: () => void;
}) {
  const [copiedCode, setCopiedCode] = useState(false);
  const connected = state.players.filter((p) => p.connected).length;
  const canStart = connected >= MIN_PLAYERS;
  const mode = state.contentMode ?? "text";

  const copyCodeOnly = async () => {
    try {
      await navigator.clipboard.writeText(state.code);
      setCopiedCode(true);
      window.setTimeout(() => setCopiedCode(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex flex-1 flex-col">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <BrandMark compact />
        </div>
        <PhaseBadge phase={state.phase} />
      </header>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mb-6"
      >
        <Card strong className="text-center">
          <p className="mb-2 text-xs tracking-[0.2em] text-[var(--color-muted)] uppercase">
            Room code
          </p>
          <button
            type="button"
            onClick={copyCodeOnly}
            className="font-mono text-4xl font-bold tracking-[0.35em] text-gradient sm:text-5xl"
            title="Copy code only"
          >
            {state.code}
          </button>
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            {copiedCode
              ? "Code copied!"
              : "Tap code to copy · friends can join anytime"}
          </p>
          <InvitePair code={state.code} className="mt-4 w-full" />
        </Card>
      </motion.div>

      <Card className="mb-4">
        <p className="mb-3 text-xs font-semibold tracking-wide text-[var(--color-muted)] uppercase">
          Game style
        </p>
        {isHost ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {MODE_OPTIONS.map((opt) => {
              const active = mode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onSetContentMode(opt.id)}
                  className={`rounded-xl border px-3 py-3 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] ${
                    active
                      ? "border-[var(--color-accent)] bg-[rgba(57,181,74,0.12)]"
                      : "border-white/10 bg-black/20 hover:bg-white/5"
                  }`}
                >
                  <span className="block text-sm font-semibold text-[var(--color-cream)]">
                    {opt.label}
                  </span>
                  <span className="mt-1 block text-[11px] leading-snug text-[var(--color-muted)]">
                    {opt.blurb}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-[var(--color-cream)]">
            {MODE_OPTIONS.find((o) => o.id === mode)?.label ?? "All text"}
            <span className="mt-1 block text-xs text-[var(--color-muted)]">
              {MODE_OPTIONS.find((o) => o.id === mode)?.blurb}
            </span>
          </p>
        )}
      </Card>

      <Card className="mb-6 flex-1">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold tracking-wide text-[var(--color-muted)] uppercase">
            Players
          </h2>
          <span className="text-sm text-[var(--color-muted)]">
            {connected} online
          </span>
        </div>
        <PlayerList
          players={state.players}
          hostId={state.hostId}
          youId={state.you.id}
        />
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row">
        {isHost ? (
          <Button
            className="flex-1"
            disabled={!canStart}
            onClick={() => onStart(mode)}
          >
            {canStart
              ? "Start the nonsense"
              : `Need ${MIN_PLAYERS - connected} more`}
          </Button>
        ) : (
          <div className="glass flex-1 rounded-xl px-4 py-3 text-center text-sm text-[var(--color-muted)]">
            Waiting for host to start…
          </div>
        )}
        <Button variant="ghost" onClick={onLeave}>
          Leave
        </Button>
      </div>
    </div>
  );
}
